import { RichTextNode, RichTextNodeKind } from '@griply/types';

import { decodeHtmlEntities } from './html-entities';

const tagRegex = /<(\/)?([a-z][\w-]*)((?:\s[^<>]*)?)\/?>/gi;
const hrefRegex = /\bhref=["']([^"']*)["']/i;

// Anything outside this list is unwrapped rather than dropped: a <span> or <font> keeps its
// text, it just stops being a node. Nothing here can carry script or style content.
const containerTags = [
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'a',
  'b',
  'strong',
  'i',
  'em',
  'u',
];

type OpenNode = { tag: string; href: string; children: RichTextNode[] };

const toNode = ({ tag, href, children }: OpenNode): RichTextNode => {
  switch (tag) {
    case 'ul':
      return { kind: RichTextNodeKind.List, ordered: false, children };
    case 'ol':
      return { kind: RichTextNodeKind.List, ordered: true, children };
    case 'li':
      return { kind: RichTextNodeKind.ListItem, children };
    case 'a':
      return { kind: RichTextNodeKind.Link, href, children };
    case 'b':
    case 'strong':
      return { kind: RichTextNodeKind.Bold, children };
    case 'i':
    case 'em':
      return { kind: RichTextNodeKind.Italic, children };
    case 'u':
      return { kind: RichTextNodeKind.Underline, children };
    case 'p':
      return { kind: RichTextNodeKind.Paragraph, children };
    default:
      return { kind: RichTextNodeKind.Block, children };
  }
};

export const parseRichText = (value: string): RichTextNode[] => {
  if (!value) {
    return [];
  }

  const root: RichTextNode[] = [];
  const open: OpenNode[] = [];

  const currentChildren = () =>
    open.length ? open[open.length - 1].children : root;

  // An unclosed tag must not swallow the rest of the document, so closing one also closes
  // everything opened inside it.
  const closeThrough = (depth: number) => {
    if (open.length <= depth) {
      return;
    }
    const node = open.pop()!;
    currentChildren().push(toNode(node));
    closeThrough(depth);
  };

  const pushText = (text: string) => {
    if (!text) {
      return;
    }
    currentChildren().push({
      kind: RichTextNodeKind.Text,
      value: decodeHtmlEntities(text),
    });
  };

  let lastIndex = 0;

  [...value.matchAll(tagRegex)].forEach((match) => {
    const matchIndex = match.index ?? 0;
    pushText(value.substring(lastIndex, matchIndex));
    lastIndex = matchIndex + match[0].length;

    const isClosing = !!match[1];
    const tag = match[2].toLowerCase();

    if (tag === 'br') {
      !isClosing && currentChildren().push({ kind: RichTextNodeKind.Break });
      return;
    }

    if (!containerTags.includes(tag)) {
      return;
    }

    if (!isClosing) {
      open.push({
        tag,
        href: match[3]?.match(hrefRegex)?.[1] ?? '',
        children: [],
      });
      return;
    }

    const openIndex = open.reduce(
      (found, node, index) => (node.tag === tag ? index : found),
      -1,
    );
    openIndex !== -1 && closeThrough(openIndex);
  });

  pushText(value.substring(lastIndex));
  closeThrough(0);

  return root;
};
